/**
 * Public booking.
 *
 * Same journey as the legacy page — find your building, open the slots, take
 * the earliest free one — with one difference that matters: a slot taken by a
 * colleague greys out here without a reload, because the server pushes it.
 */
import { useMemo, useState } from "react";
import { formatDayFr, isSlotBookable, type PublicSlot, type SiteSummary } from "@soi/contracts";
import { useMe, useSite, useSites, useZones } from "../lib/queries.js";
import { BookingDialog } from "../components/BookingDialog.js";

interface Target {
  slot: PublicSlot;
  day: string;
  siteName: string;
  siteSlug: string;
  intervenantName: string;
}

function SiteCard({
  site,
  isAdmin,
  showPast,
}: {
  site: SiteSummary;
  isAdmin: boolean;
  showPast: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<Target | null>(null);
  // Slots are only fetched once the card is opened: 69 buildings' worth of
  // slots up front would be 3051 rows nobody has asked to see.
  const { data: detail, isLoading } = useSite(open ? site.slug : null);

  // "Past" is by calendar day: a whole day block is hidden once it is before
  // today. Today itself stays — some of its slots may still be bookable.
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const visibleDays = useMemo(() => {
    if (!detail) return [];
    return showPast ? detail.days : detail.days.filter((d) => d.day >= todayIso);
  }, [detail, showPast, todayIso]);
  const hiddenPastCount = detail ? detail.days.length - visibleDays.length : 0;

  return (
    <article className="card" data-testid={`site-${site.slug}`}>
      <div className="head">
        <h2 className="cname">
          {site.name}
          {site.lot === "Police" ? (
            <span className="tag pol">Corps de Police</span>
          ) : (
            <span className="tag soi">{site.zone}</span>
          )}
        </h2>
        <p className="cmeta">
          Du <span className="daterange">{formatDayFr(site.firstDay)}</span> au{" "}
          <span className="daterange">{formatDayFr(site.lastDay)}</span>
          {" · "}
          <span data-testid={`free-${site.slug}`}>{site.freeSlots}</span> créneaux libres sur{" "}
          {site.totalSlots}
        </p>
      </div>

      <details open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
        <summary>
          <span>Choisir mon créneau</span>
        </summary>

        {isLoading && <p className="jour">Chargement des créneaux…</p>}

        {detail && (
          <>
            <p className="jour hint">
              Privilégiez le premier créneau encore libre : les horaires suivent l'ordre de la
              tournée.
            </p>
            {!showPast && hiddenPastCount > 0 && (
              <p className="jour hint">
                {hiddenPastCount} date{hiddenPastCount > 1 ? "s" : ""} passée
                {hiddenPastCount > 1 ? "s" : ""} masquée{hiddenPastCount > 1 ? "s" : ""}.
              </p>
            )}
            {visibleDays.length === 0 && (
              <p className="jour hint">
                Toutes les dates de ce bâtiment sont passées. Cochez « Afficher les dates
                passées » pour les voir.
              </p>
            )}
            {visibleDays.map((day) => {
              const count = day.intervenants.reduce((n, i) => n + i.slots.length, 0);
              return (
                <div className="jour" key={day.day}>
                  <div className="jtitre">
                    <b>{formatDayFr(day.day)}</b>
                    <em>{count} créneaux</em>
                  </div>
                  {day.intervenants.map((group) => (
                    <div className="iv" key={group.code}>
                      <div className="ivn">{group.displayName}</div>
                      <div className="cr">
                        {group.slots.map((slot) => {
                          // Checked per slot, not per day: the 24h notice
                          // cutoff falls mid-day, in local time — a slot
                          // just past it stays bookable even "today".
                          // An admin bypasses the notice entirely — see
                          // POST /api/reservations, which enforces the same
                          // exception server-side.
                          const wouldBeTooSoon = !isSlotBookable(day.day, slot.time);
                          const tooSoon = wouldBeTooSoon && !isAdmin;
                          // A superseded slot was replaced by a manual add: it
                          // stays visible for context but can never be booked,
                          // by anyone, admin included.
                          const disabled = slot.taken || tooSoon || slot.superseded;
                          return (
                            <button
                              key={slot.id}
                              type="button"
                              className={`slot${disabled ? " todo" : ""}`}
                              data-testid={`slot-${slot.id}`}
                              data-taken={slot.taken ? "true" : "false"}
                              data-superseded={slot.superseded ? "true" : "false"}
                              disabled={disabled}
                              title={
                                slot.superseded
                                  ? "Ce créneau a été remplacé et n'est plus disponible"
                                  : slot.taken
                                    ? "Ce créneau est déjà réservé"
                                    : tooSoon
                                      ? "Ce créneau est trop proche : réservez au moins 24h à l'avance"
                                      : wouldBeTooSoon
                                        ? "Créneau passé ou trop proche — réservation admin"
                                        : `Réserver le ${formatDayFr(day.day)} à ${slot.time} avec ${group.displayName}`
                              }
                              onClick={() =>
                                setTarget({
                                  slot,
                                  day: day.day,
                                  siteName: site.name,
                                  siteSlug: site.slug,
                                  intervenantName: group.displayName,
                                })
                              }
                            >
                              {slot.time}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </>
        )}
      </details>

      {target && <BookingDialog target={target} onClose={() => setTarget(null)} />}
    </article>
  );
}

export function PublicBooking() {
  const { data: sites, isLoading, isError } = useSites();
  const { data: zones } = useZones();
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";
  const [search, setSearch] = useState("");
  const [zone, setZone] = useState("");
  const [showPast, setShowPast] = useState(false);

  const filtered = useMemo(() => {
    if (!sites) return [];
    const needle = search.trim().toLowerCase();
    return sites.filter(
      (s) =>
        (!needle || `${s.name} ${s.zone}`.toLowerCase().includes(needle)) &&
        (!zone || s.zone === zone),
    );
  }, [sites, search, zone]);

  return (
    <div>
      <header className="hero">
        <div className="wrap">
          <p className="eyebrow">Votre rendez-vous</p>
          <h1>Votre nouveau poste de travail vous attend. Réservez votre créneau.</h1>
          <p className="lede">
            Trouvez votre bâtiment et réservez de préférence le premier créneau encore libre.
            L'intervention dure 45 minutes et en votre présence.
          </p>
          <p className="steps-lab">Comment réserver</p>
          <ol className="steps">
            <li>
              <span className="num">1</span>
              <b>Trouvez votre bâtiment</b>
              <span>Cherchez son nom ou son adresse ci-dessous.</span>
            </li>
            <li>
              <span className="num">2</span>
              <b>Ouvrez les créneaux</b>
              <span>Repérez la période prévue pour votre site.</span>
            </li>
            <li>
              <span className="num">3</span>
              <b>Choisissez une heure</b>
              <span>Privilégiez le premier créneau disponible.</span>
            </li>
            <li>
              <span className="num">4</span>
              <b>C'est réservé</b>
              <span>Vous recevez une confirmation par courriel.</span>
            </li>
          </ol>
          <p className="nudge">
            <b>Privilégiez le premier créneau disponible.</b> Les horaires sont proposés dans
            l'ordre de la tournée. En sélectionnant le créneau disponible le plus proche, vous
            limitez les déplacements du technicien et contribuez à équiper l'ensemble du bâtiment
            plus rapidement.
          </p>
        </div>
      </header>

      <div className="tools">
        <div className="wrap">
          <input
            type="search"
            placeholder="Rechercher votre bâtiment ou votre rue…"
            aria-label="Rechercher"
            autoComplete="off"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            aria-label="Filtrer par lieu"
            value={zone}
            onChange={(e) => setZone(e.target.value)}
          >
            <option value="">Tous les lieux</option>
            {zones?.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
          <label
            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: ".83rem", cursor: "pointer" }}
          >
            <input
              type="checkbox"
              checked={showPast}
              onChange={(e) => setShowPast(e.target.checked)}
              data-testid="toggle-past"
            />
            Afficher les dates passées
          </label>
          <span className="count" data-testid="site-count">
            {filtered.length} {filtered.length > 1 ? "bâtiments" : "bâtiment"}
          </span>
        </div>
      </div>

      <main>
        <div className="wrap">
          {isLoading && <p className="empty">Chargement des bâtiments…</p>}
          {isError && (
            <p className="empty">
              Le service de réservation est momentanément indisponible. Réessayez dans quelques
              instants ou écrivez à windows11@lausanne.ch.
            </p>
          )}
          <div className="grid">
            {filtered.map((site) => (
              <SiteCard key={site.slug} site={site} isAdmin={isAdmin} showPast={showPast} />
            ))}
          </div>
          {!isLoading && !isError && filtered.length === 0 && (
            <div className="empty">Aucun bâtiment ne correspond. Essayez le nom de votre rue.</div>
          )}
        </div>
      </main>

      <footer>
        <div className="wrap">
          <div className="fgrid">
            <section>
              <h2>Avant votre rendez-vous</h2>
              <ul>
                <li>Enregistrez vos documents sur le lecteur réseau.</li>
                <li>Gardez vos identifiants applicatifs sous la main.</li>
                <li>Dégagez votre plan de travail.</li>
                <li>Restez à votre place : l'intervention ne dure que 45 minutes.</li>
              </ul>
            </section>
            <section>
              <h2>Un imprévu ?</h2>
              <p>
                Prévenez-nous au plus vite : votre créneau sera proposé à un collègue et vous
                pourrez en choisir un autre.
              </p>
              <p className="mail">
                <a href="mailto:windows11@lausanne.ch">windows11@lausanne.ch</a>
              </p>
            </section>
            <section>
              <h2>Une question ?</h2>
              <p>
                Le Service d'organisation et d'informatique répond à vos questions sur le
                remplacement de votre poste de travail.
              </p>
              <p className="mail">
                <a href="mailto:windows11@lausanne.ch">windows11@lausanne.ch</a>
              </p>
            </section>
          </div>
          <p className="fnote">
            Déploiement assuré par{" "}
            <a href="https://www.digital-solutions.io/" target="_blank" rel="noopener">
              Digital Solutions SA
            </a>{" "}
            pour le Service d'organisation et d'informatique · Ville de Lausanne
          </p>
        </div>
      </footer>
    </div>
  );
}
